// ADV-264 — data helpers for the redesigned orders page (/account/my-orders).
//
// Every value the page shows comes from the order itself (the odoo-sync lead), its car, the
// car's seller (the dealer info the checkout shows too) or — for the rates of a financing/leasing
// order and its Finanzierungs-/Leasingdetails layer — the calculation the checkout saved with the
// order (contractTermsOf). Fields none of them carry (Widerruf deadline, ...) are left out instead
// of being filled with a placeholder.
import { getFormattedPrice } from '@bytebrand/fe-ui-core-autobahn/utils';
import { getAnschlussGarantiePrice } from '../../../stores/CheckoutStore';
const STEP_INDEX = {
    order_received: 0,
    preparation: 1,
    registration: 2,
    delivery: 3,
    pick_up: 3,
    handing_over: 4,
};
/** 'selector_status_preparation' → 'preparation' (the key of the existing orderStatus texts). */
export const statusKey = (status) => String(status || '').replace(/^selector_status_/, '');
export const buyingTypeOf = (lead) => lead.buyingType === 'financing' || lead.buyingType === 'leasing' ? lead.buyingType : 'buy';
export const isWon = (lead) => lead.wonStatus === 'won';
export const hasCar = (lead) => !!lead && typeof lead.car === 'object' && lead.car !== null;
/**
 * The five Bestellstatus steps; the fourth is Abholung instead of Lieferung for a self-pickup order
 * (selfPickup: the checkout's choice when known, see checkoutAddressesOf).
 */
export const orderSteps = (lead, selfPickup = !!lead.selfPickup) => {
    const key = statusKey(lead.status);
    const current = key in STEP_INDEX ? STEP_INDEX[key] : -1;
    const fourth = key === 'pick_up' || (key !== 'delivery' && selfPickup) ? 'pick_up' : 'delivery';
    const keys = ['order_received', 'preparation', 'registration', fourth, 'handing_over'];
    return keys.map((stepKey, i) => ({
        key: stepKey,
        state: i < current ? 'done' : i === current ? 'current' : 'open',
    }));
};
const isAmount = (value) => typeof value === 'number' && Number.isFinite(value);
/** Positive amount (a cost line exists); 0 / missing means the order has no such line. */
export const hasAmount = (value) => isAmount(value) && value > 0;
/** Any amount including 0 (a deposit of 0 € is still a value the order states). */
export const hasValue = isAmount;
/** "1.000,99 €" — table rows, always with cents. */
export const euro = (value) => getFormattedPrice(value, '$,.2f', ' €');
/**
 * "599 €" / "25.599 €" — the headline figures and the monthly amounts, in whole euros rounded as
 * the checkout and the VDP show them (Math.round: 296,86 → "297 €", 24,97 → "25 €"). One-off
 * amounts in the table keep the cents (euro).
 */
export const euroShort = (value) => getFormattedPrice(Math.round(value), '$,.0f', ' €');
/** "10.000 km" */
export const kilometers = (value) => getFormattedPrice(value, '$,.0f', ' km');
/** "6,99 %" — as the VDP's Repräsentatives Beispiel (fe-ui-core-autobahn formatPct). */
export const percent = (value) => `${value.toFixed(2).replace('.', ',')} %`;
/** "14.02.2023" */
export const germanDay = (date) => date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
export const germanDate = (value) => {
    if (!value)
        return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    return germanDay(date);
};
const words = (...parts) => parts.map((part) => String(part || '').trim()).filter(Boolean).join(' ');
const commaList = (...parts) => parts.filter(Boolean).join(', ');
/** "Hannes Käther, Wittenberger Str. 81, 04129 Leipzig" */
// Odoo's address of an order without a saved checkout. Its street already carries the house
// number ("Musterstr., 1"), and odoo-sync fills customerAddress.number with the phone number
// (leads.service.ts formatOdooLead), so number is left out.
export const customerAddressLine = (address) => address
    ? commaList(words(address.firstname, address.lastname), words(address.street), words(address.zip, address.city))
    : '';
export const deliveryAddressLine = (address) => address ? commaList(words(address.partner), words(address.street, address.number), words(address.zip, address.city)) : '';
/** First line of the vehicle block: make + model, as on the current page. */
export const carTitle = (car) => words(car.mainData && car.mainData.make, car.mainData && car.mainData.model);
export const carSubtitle = (car) => words(car.mainData && car.mainData.subModel);
// the financing figures of the contract link, fe-financing names as content-doc-generator reads them
const CONTRACT_FIGURES = [
    'monthlyInstallment',
    'firstInstallment',
    'onetimeSpecialPayment',
    'purchasePrice',
    'netLoanAmount',
    'paymentProtectionInsurance',
    'finalInstallment',
    'grossLoanAmount',
    'grossLoanAmountWithoutFinalInstallment',
    'nominalInterestRate',
    'annualPercentageRate',
    'paybackPeriod',
];
/** amounts and rates with cents, as Odoo's link has them */
const linkNumber = (value) => {
    const parsed = numberOf(value);
    return parsed === undefined ? undefined : Math.round(parsed * 100) / 100;
};
/** "Tom Helm" → "Tom_Helm", Odoo's bot included, as in Odoo's link (content-doc-generator turns _ back into spaces) */
const salespersonOf = (name) => {
    const clean = String(name || '').trim();
    return clean ? clean.replace(/\s+/g, '_') : undefined;
};
// The order's own figures (the odoo-sync lead) by their name in the link
const LEAD_FIGURES = {
    monthlyInstallment: (lead) => lead.monthlyInstallment,
    firstInstallment: (lead) => lead.deposit,
    onetimeSpecialPayment: (lead) => lead.onetimeSpecialPayment,
    purchasePrice: (lead) => lead.purchasePrice,
    netLoanAmount: (lead) => lead.netLoanAmount,
    paymentProtectionInsurance: (lead) => lead.paymentProtectionInsuranceCost,
    finalInstallment: (lead) => lead.finalInstallment,
    grossLoanAmount: (lead) => lead.grossLoanAmount,
    grossLoanAmountWithoutFinalInstallment: (lead) => lead.grossLoanAmountWithoutFinalInstallment,
    nominalInterestRate: (lead) => lead.nominalInterestRate,
    annualPercentageRate: (lead) => lead.annualPercentageRate,
    paybackPeriod: (lead) => lead.paybackPeriod,
};
/** Unix seconds of the offer: Odoo's lead (when odoo-sync stored it), else the saved order ("Jetzt kaufen") */
const offerDateOf = (lead, contract) => {
    const ownContract = contract && contract.orderId && contract.orderId === lead.orderId ? contract : null;
    const value = lead.createdAt || (ownContract ? ownContract.updatedAt : undefined);
    const time = value ? Date.parse(value) : NaN;
    return Number.isFinite(time) ? Math.floor(time / 1000) : undefined;
};
/** The filled-in Ankaufsprüfung answers as JSON (the generator's own filter), none → left out */
const conditionFactsOf = (lead) => {
    const facts = (Array.isArray(lead.conditionFacts) ? lead.conditionFacts : [])
        .filter((fact) => Array.isArray(fact) && String(fact[0] || '').trim() && String(fact[1] || '').trim())
        .map(([label, value]) => [String(label).trim(), String(value).trim()]);
    return facts.length ? JSON.stringify(facts) : undefined;
};
/**
 * The rates of a financing/leasing order: the calculation the checkout saved when it still matches
 * the order (contractTermsOf), else what the order itself states (sales changed it in Odoo). Every
 * figure of Odoo's link is sent, 0 when there is no value.
 */
const contractFiguresOf = (lead, contract) => {
    const type = buyingTypeOf(lead);
    if (type === 'buy')
        return [];
    const offer = contract && contract.financingConfig && contractTermsOf(lead, type, contract)
        ? contract.financingConfig[type]
        : null;
    const figureOf = (key) => (offer ? offer[key] : LEAD_FIGURES[key] && LEAD_FIGURES[key](lead));
    const figures = CONTRACT_FIGURES.map((key) => [key, linkNumber(figureOf(key)) ?? 0]);
    return offer ? [...figures, ['withAddServices', !!offer.withAddServices]] : figures;
};
/**
 * "Kfz-Kaufvertrag inkl. Anlagen": the purchase contract PDF that content-doc-generator
 * (type purchase_contract, the contract group only) builds with the same parameters as Odoo's
 * contract link: the car's seller (userId) and the buyer (requestUserId), the order's request
 * number, salesperson, price and date, the rates the checkout saved with it and the car's condition
 * (Ankaufsprüfung). A rate without a value goes as 0; any other value the order doesn't have is left
 * out, the generator then uses its own default.
 */
export const contractUrl = (lead, contract) => {
    if (!lead.user)
        return '';
    const params = [
        ['type', 'purchase_contract'],
        ['parts', 'contract'],
        ['offerId', lead.request],
        ['carId', lead.car._id],
        // the seller's data fills the Verkäufer block; the buyer's only as a last resort, as before
        ['userId', lead.dealer || sellerIdOf(lead) || lead.user],
        ['requestUserId', lead.user],
        ['salesperson', salespersonOf(lead.salesPerson)],
        ['buyingType', buyingTypeOf(lead)],
        ['currentSalesPrice', linkNumber(lead.currentSalesPrice)],
        ['offerDate', offerDateOf(lead, contract)],
        ...contractFiguresOf(lead, contract),
        ['conditionFacts', conditionFactsOf(lead)],
    ];
    const query = params
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);
    return `/service/content-doc-generator/documents/get?${query.join('&')}`;
};
/** Lender (financing) or lessor (leasing) as on the CDP: the car's own bank, else the platform default. */
export const lenderOf = (car, kind, t) => kind === 'financing'
    ? (car.rate && car.rate.financialInstitution) || t('autobahn:modal.lenderName')
    : (car.leasing && car.leasing.financialInstitution) || t('autobahn:modal.leaseLenderName');
/** "Open Bank AG, Santander-Platz 1, 41061 Mönchengladbach" → "Open Bank AG" */
export const companyName = (lender) => lender.split(',')[0].trim();
const numberOf = (value) => {
    const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return isAmount(parsed) ? parsed : undefined;
};
const positive = (value) => {
    const parsed = numberOf(value);
    return parsed !== undefined && parsed > 0 ? parsed : undefined;
};
/** Same amount, give or take the rounding of the two systems. */
const sameAmount = (a, b) => Math.abs(a - b) < 0.02;
/** The 2. Kreditnehmer / Leasingnehmer as the Bestellübersicht lists them: only a fully filled form. */
const secondPartyOf = (info) => {
    if (!info || typeof info !== 'object')
        return '';
    const values = Object.keys(info).map((key) => info[key]);
    if (!values.length || values.some((value) => value === '' || value === 'invalid'))
        return '';
    return words(info.firstName, info.lastName);
};
/** Anschluss-Garantie choice of the checkout (0 = +2 Jahre, 1 = +1 Jahr, 2 = keine) → years */
const warrantyYearsOf = (choice) => (choice === 0 ? 2 : choice === 1 ? 1 : 0);
/**
 * The Anschluss-Garantie of a saved calculation, priced as the checkout prices it (CheckoutStore,
 * checkout offer index: financing 0, leasing 1, buy 3 — monthly, monthly, once). null when a
 * chosen warranty can't be priced: it is never free, so the split would be wrong.
 */
const warrantyOf = (config, checkoutOfferIndex) => {
    const choice = numberOf((config.checkout || {}).anschlussGarantie);
    const years = warrantyYearsOf(choice);
    if (!years)
        return { years: 0, price: 0 };
    let price = 0;
    try {
        price = getAnschlussGarantiePrice(choice, config, checkoutOfferIndex);
    }
    catch (e) {
        price = 0;
    }
    return hasAmount(price) ? { years, price } : null;
};
/**
 * The calculation the checkout saved with this order, or null when it can't be used: none was
 * saved, it belongs to another checkout of the car, or the order's own terms no longer match it
 * (sales can change term, deposit or rate in Odoo after the order; Odoo's rate is the vehicle
 * rate plus the checkout's monthly services, checkout.totalCost).
 * The rates are split as the checkout's Bestellübersicht splits them: vehicle rate + Anschluss-
 * Garantie + financed Rundum-Sorglos-Paket = Gesamtrate (Odoo's rate has the Zulassungsservice of
 * checkout.totalCost instead of the Anschluss-Garantie, so it is not what the customer saw).
 */
export const contractTermsOf = (lead, kind, contract) => {
    const config = contract ? contract.financingConfig : null;
    const offer = config ? config[kind] : null;
    if (!contract || !config || !offer)
        return null;
    if (lead.orderId && contract.orderId !== lead.orderId)
        return null;
    const term = positive(offer.paybackPeriod);
    const down = numberOf(offer.firstInstallment);
    const monthly = positive(offer.monthlyInstallment);
    if (term === undefined || down === undefined || monthly === undefined)
        return null;
    const services = numberOf(config.checkout && config.checkout.totalCost) || 0;
    if (lead.paybackPeriod !== term)
        return null;
    if (!hasValue(lead.monthlyInstallment) || !sameAmount(lead.monthlyInstallment, monthly + services))
        return null;
    if (hasValue(lead.deposit) && !sameAmount(lead.deposit, down))
        return null;
    const common = config.common || {};
    const withAddServices = !!offer.withAddServices;
    const addServicesCostRaw = positive(offer.addServicesCostRaw);
    const salesPrice = positive(common.currentSalesPrice) || positive(offer.currentSalesPrice) || 0;
    const basePrice = positive(common.basePrice) || salesPrice;
    if (!basePrice)
        return null;
    const netLoanAmount = numberOf(offer.netLoanAmount);
    const checkout = config.checkout || {};
    const warranty = warrantyOf(config, kind === 'leasing' ? 1 : 0);
    if (!warranty)
        return null;
    const warrantyYears = warranty.years;
    const warrantyMonthly = warranty.price;
    const packageMonthly = withAddServices ? positive(offer.addServicesCost) || 0 : 0;
    return {
        price: basePrice + (withAddServices && addServicesCostRaw ? addServicesCostRaw : 0),
        down,
        term,
        monthly,
        apr: positive(offer.annualPercentageRate),
        nominalRate: positive(offer.nominalInterestRate),
        netLoanAmount: netLoanAmount !== undefined ? netLoanAmount : Math.max(0, salesPrice - down),
        finalInstallment: positive(offer.finalInstallment),
        totalFinancingAmount: positive(offer.totalFinancingAmount) || positive(offer.grossLoanAmount),
        annualMileage: positive(offer.annualMileage),
        withAddServices,
        addServicesCostRaw,
        listPrice: positive(common.recomendedRetailPrice) || positive(lead.car.price && lead.car.price.recomendedRetailPrice),
        lender: String(offer.financialInstitution || '').trim() || undefined,
        total: monthly + warrantyMonthly,
        vehicleRate: monthly - packageMonthly,
        warrantyYears,
        warrantyMonthly,
        packageMonthly,
        secondParty: secondPartyOf(checkout.borrowerInformation),
        purchasePrice: positive(common.currentSalesPrice),
    };
};
/**
 * The purchase the checkout saved with this order, or null when it can't be used: none was saved,
 * it belongs to another checkout of the car, or sales changed the price in Odoo since. Odoo lists
 * the package once more as "Aufbereitungskosten" (vehicle_preparation_costs_vk, the platform
 * preparation costs = the Rundum-Sorglos-Paket) on top of a price that already includes it, so its
 * product sum is not what the customer saw.
 */
export const purchaseTermsOf = (lead, contract) => {
    const config = contract ? contract.financingConfig : null;
    if (!contract || !config)
        return null;
    if (lead.orderId && contract.orderId !== lead.orderId)
        return null;
    const common = config.common || {};
    const price = positive(common.currentSalesPrice);
    if (price === undefined)
        return null;
    if (hasAmount(lead.currentSalesPrice) && !sameAmount(lead.currentSalesPrice, price))
        return null;
    const warranty = warrantyOf(config, 3);
    if (!warranty)
        return null;
    // the package baked into the sales price: what basePrice leaves out, else its own amount
    const basePrice = positive(common.basePrice);
    const listed = positive(common.buyAddServicesCost) || positive((config.buy || {}).addServicesCost) || 0;
    const pack = basePrice !== undefined && basePrice <= price ? price - basePrice : Math.min(listed, price);
    return { vehiclePrice: price - pack, package: pack, warrantyYears: warranty.years, warranty: warranty.price, total: price + warranty.price };
};
/** The total of a purchase: the checkout's Gesamtkaufpreis when its saved calculation matches, else Odoo's. */
export const purchaseTotalOf = (lead, contract) => {
    const terms = purchaseTermsOf(lead, contract);
    return terms ? terms.total : lead.totalPrice;
};
/**
 * The monthly figure of a financing/leasing order: the checkout's Gesamtrate when its saved
 * calculation matches the order, else the order's own rate (Odoo). undefined for a purchase.
 */
export const monthlyRateOf = (lead, contract) => {
    const type = buyingTypeOf(lead);
    if (type === 'buy')
        return undefined;
    const terms = contractTermsOf(lead, type, contract);
    return terms ? terms.total : lead.monthlyInstallment;
};
// "Hannes Käther, Wittenberger Str. 81, 04129 Leipzig" from the checkout's contact / delivery form
const personAddressLine = (info) => info && typeof info === 'object'
    ? commaList(words(info.firstName, info.lastName), words(info.street, info.number), words(info.zip, info.city))
    : '';
/**
 * The addresses and the delivery choice the customer entered in the checkout for this order, or
 * null when its saved record belongs to another checkout of the car. Odoo's copy is not reliable:
 * odoo-sync puts the phone number into customerAddress.number, and leasing/purchase orders came
 * back as self-pickup although delivery was chosen (offer's withTransportation).
 */
export const checkoutAddressesOf = (lead, contract) => {
    const config = contract ? contract.financingConfig : null;
    if (!contract || !config)
        return null;
    if (lead.orderId && contract.orderId !== lead.orderId)
        return null;
    const checkout = config.checkout || {};
    const billing = personAddressLine(checkout.contactInformation);
    if (!billing)
        return null;
    const offer = config[buyingTypeOf(lead)] || {};
    const selfPickup = !offer.withTransportation;
    // the delivery form starts as the contact address
    const delivery = selfPickup ? '' : personAddressLine(checkout.deliveryInformation) || billing;
    return { billing, delivery, selfPickup };
};
/** The seller from the dealer info the checkout loads too (dealers/{ownerId}/info). */
export const sellerOf = (info) => {
    const address = info && info.dealerMainAddress;
    const name = address && String(address.company || '').trim();
    if (!name)
        return null;
    return { name, address: commaList(words(address.street, address.number), words(address.zip, address.city)) };
};
/** The car owner whose dealer info names the seller. */
export const sellerIdOf = (lead) => (lead.car.metaData && lead.car.metaData.ownerId) || '';
/**
 * End of the Widerrufsfrist. The order carries neither the contract date nor a deadline, so it
 * is unknown for now: the "endet am …" sentence is left out and the expired state never shows.
 */
export const withdrawalDeadline = (_lead) => null;
/**
 * Leasing is withdrawn from the lessor directly (Hinweis). Purchase and financing get the
 * button until the period has run out, a checkout still in progress too (its Odoo request is
 * marked). A handed-over order shows the block only once it has expired, as in Figma's
 * handed-over card.
 */
export const withdrawalStateOf = (lead) => {
    if (buyingTypeOf(lead) === 'leasing')
        return isWon(lead) ? null : 'notice';
    const deadline = withdrawalDeadline(lead);
    if (deadline && deadline.getTime() < Date.now())
        return 'expired';
    return isWon(lead) ? null : 'action';
};
